import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarClock, Pencil } from 'lucide-react';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { FloatingLabelInput } from '@/shared/components/custom/form-field/FloatingLabelInput';
import { Pagination } from '@/shared/components/custom/pagination';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { formatTimeAgo } from '@/shared/utils/formatTimeAgo';
import { computeVisiblePages } from '@/shared/utils/paginationUtils';
import { DEFAULT_PAGE_SIZE } from '@/shared/types/pagination';
import { useMemberWorkSchedules } from '../hooks/useMemberWorkSchedules';
import { MemberWorkScheduleDialog } from './MemberWorkScheduleDialog';

/**
 * Paginated, searchable list of member work schedule overrides.
 * Each row opens `MemberWorkScheduleDialog` for editing or removal.
 */
export function WorkScheduleOverridesList() {
  const { t, i18n } = useTranslation('workSchedules');

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = DEFAULT_PAGE_SIZE;
  const [editingUserId, setEditingUserId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useMemberWorkSchedules(
    debouncedSearch,
    pageIndex,
    pageSize,
  );

  const items = data?.items ?? [];
  const totalItems = data?.total ?? 0;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 0;

  const pagination = {
    pageIndex,
    pageSize,
    totalItems,
    totalPages,
    canPrevious: pageIndex > 0,
    canNext: totalPages > 0 && pageIndex < totalPages - 1,
    visiblePages: computeVisiblePages(pageIndex, totalPages),
    goToPage: (page: number) => {
      if (totalPages > 0) setPageIndex(Math.max(0, Math.min(page, totalPages - 1)));
    },
    next: () => setPageIndex((p) => (totalPages > 0 ? Math.min(p + 1, totalPages - 1) : p)),
    previous: () => setPageIndex((p) => Math.max(p - 1, 0)),
    first: () => setPageIndex(0),
    last: () => totalPages > 0 && setPageIndex(totalPages - 1),
    changePageSize: () => setPageIndex(0),
  };

  return (
    <div className="space-y-4" data-testid="work-schedules-overrides-list">
      <div>
        <h3 className="text-base font-semibold md:text-lg">{t('overrides.title')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{t('overrides.description')}</p>
      </div>

      <div className="max-w-sm">
        <FloatingLabelInput
          id="work-schedules-overrides-search"
          label={t('overrides.searchPlaceholder')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPageIndex(0);
          }}
          hasValue={!!search}
          data-testid="work-schedules-overrides-search"
        />
      </div>

      {isLoading ? (
        <div className="space-y-3" data-testid="work-schedules-overrides-loading">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        <div
          data-testid="work-schedules-overrides-error"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-4"
        >
          <p className="text-sm text-destructive">{t('overrides.loadError')}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
            {t('member.cancel')}
          </Button>
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="work-schedules-overrides-empty"
          className="rounded-lg border border-dashed py-12 text-center"
        >
          <CalendarClock className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <h4 className="text-base font-semibold">{t('overrides.empty')}</h4>
          <p className="mt-1 text-sm text-muted-foreground">{t('overrides.emptyDescription')}</p>
        </div>
      ) : (
        <>
          <ul className="space-y-2">
            {items.map((item) => {
              const userId = item.userId ?? '';
              return (
                <li
                  key={userId}
                  className="flex items-center justify-between gap-3 rounded-lg border p-3"
                  data-testid={`work-schedules-overrides-row-${userId}`}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {t('overrides.memberLabel', { userId })}
                    </p>
                    {item.updatedAt && (
                      <p className="text-xs text-muted-foreground">
                        {t('overrides.updatedAt', {
                          time: formatTimeAgo(item.updatedAt, i18n.language),
                        })}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge variant="secondary">
                      {t('overrides.hoursPerDay', { count: item.hoursPerDay })}
                    </Badge>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          aria-label={t('overrides.edit')}
                          onClick={() => setEditingUserId(userId)}
                          data-testid={`work-schedules-overrides-edit-${userId}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('overrides.edit')}</TooltipContent>
                    </Tooltip>
                  </div>
                </li>
              );
            })}
          </ul>
          <Pagination {...pagination} />
        </>
      )}

      {editingUserId && (
        <MemberWorkScheduleDialog
          member={{ userId: editingUserId }}
          open={!!editingUserId}
          onOpenChange={(o) => !o && setEditingUserId(null)}
        />
      )}
    </div>
  );
}
